import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowUpRight, Sparkles, CheckCircle2 } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import { findCatalogEntry } from "@/constants/cosecCatalog";

export default function FeaturePage({ entry }) {
  // If the caller passes `relatedEntries`, use them as-is. Otherwise resolve from the main catalog.
  const related = entry.relatedEntries
    ? entry.relatedEntries
    : (entry.related || []).map((s) => findCatalogEntry(s)).filter(Boolean);
  const relatedPrefix = entry.relatedHrefPrefix || "";

  return (
    <SiteLayout>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-60 pointer-events-none" />
        <div className="container-cv relative pt-14 md:pt-24 pb-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="pill"><Sparkles className="h-3.5 w-3.5 text-accent" /> {entry.eyebrow}</span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[3.5rem] font-bold text-primary leading-[1.04]">
              {entry.title}{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-accent">{entry.highlight}</span>
                <span className="absolute left-0 bottom-1 h-3 w-full bg-accent/30 -z-0 rounded-sm" />
              </span>
            </h1>
            <p className="mt-5 font-display text-lg md:text-xl font-semibold text-primary/80 leading-snug max-w-2xl">{entry.subhead}</p>
            <p className="mt-5 text-base md:text-[17px] text-muted-foreground leading-relaxed max-w-2xl">{entry.intro}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button asChild data-testid="feature-cta-demo" className="h-12 px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
                <Link to="/contact">Book a Demo <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild variant="outline" className="h-12 px-6 border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground font-semibold">
                <Link to="/cosecapp">All features</Link>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-br from-accent/20 via-transparent to-primary/10 rounded-3xl blur-2xl" />
              <div className="relative rounded-3xl border border-border bg-white p-3">
                <img src={entry.image} alt={entry.title} className="rounded-2xl h-72 w-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sub-features */}
      {entry.subFeatures?.length > 0 && (
        <section className="py-20 md:py-24 bg-secondary/40 border-y border-border">
          <div className="container-cv">
            <div className="max-w-3xl">
              <span className="section-eyebrow">What it does</span>
              <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-primary tracking-tight leading-[1.08]">Inside the module</h2>
            </div>
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {entry.subFeatures.map((sf, i) => (
                <div key={i} className="rounded-2xl border border-border bg-white p-6 hover:-translate-y-1 hover:border-accent/60 hover:shadow-md transition-all">
                  <div className="h-11 w-11 grid place-items-center rounded-xl bg-primary/10 text-primary">
                    <sf.Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-display text-base font-bold text-primary tracking-tight">{sf.t}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{sf.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Mini-stat strip */}
      <section className="py-20 md:py-24">
        <div className="container-cv grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <span className="section-eyebrow">Why it matters</span>
            <h2 className="mt-3 font-display text-2xl md:text-4xl font-bold text-primary tracking-tight leading-[1.08]">
              Built for UK statute. Audited end-to-end.
            </h2>
            <ul className="mt-6 space-y-3 text-primary">
              {[
                "Companies House software gateway",
                "ECCT Act 2023 ready",
                "ISO 27001 + ISO 27701 certified",
                "ISO 27001 + GDPR",
              ].map((t, i) => (
                <li key={i} className="flex items-center gap-3"><span className="lime-tick"><CheckCircle2 className="h-4 w-4" /></span>{t}</li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-3xl border border-border bg-white p-6 md:p-8 grid grid-cols-3 gap-4">
              {[
                { k: "247", l: "UK entities" },
                { k: "312", l: "Filings filed" },
                { k: "18 hrs", l: "Admin saved / mo" },
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <div className="font-display text-3xl font-bold text-primary leading-tight">{s.k}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-20 md:py-24 bg-secondary/40 border-y border-border">
          <div className="container-cv">
            <div className="flex items-end justify-between mb-8">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-primary tracking-tight">Related modules</h2>
              <Link to="/cosecapp" className="hidden md:inline-flex items-center gap-1 text-sm font-bold text-primary hover:text-accent">All features <ArrowRight className="h-3.5 w-3.5" /></Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((r) => (
                <Link key={r.slug} to={relatedPrefix ? `${relatedPrefix}/${r.slug}` : `/${r.slug}`} className="group rounded-2xl border border-border bg-white p-6 hover:-translate-y-1 hover:border-accent/60 hover:shadow-md transition-all">
                  <div className="flex items-center justify-between">
                    <div className="h-11 w-11 grid place-items-center rounded-xl bg-primary/10 text-primary group-hover:bg-accent group-hover:text-primary transition-colors">
                      <r.Icon className="h-5 w-5" />
                    </div>
                    <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-bold text-primary tracking-tight">{r.title.replace(/[-,.]$/, "")} <span className="text-accent">{r.highlight}</span></h3>
                  <p className="mt-1.5 text-sm text-muted-foreground line-clamp-2">{r.subhead}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-[0.07] pointer-events-none" />
        <div className="container-cv relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.04]">
              Try {entry.eyebrow} live.{" "}
              <span className="text-accent">No commitment.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
            <Button asChild className="h-12 px-6 bg-accent hover:bg-accent/90 text-primary font-bold">
              <Link to="/contact">Book a Demo <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline" className="h-12 px-6 border-white/30 text-white hover:bg-white hover:text-primary font-bold">
              <Link to="/pricing">See pricing</Link>
            </Button>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
